import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Check, 
  Star, 
  TrendingUp, 
  Building2, 
  Brain, 
  MapPin, 
  ArrowRight,
  ChevronDown,
  ChevronUp,
  X as LucideX,
  Clock,
  ArrowRight as LucideArrowRight,
  DollarSign,
  User,
  Link as LucideLink,
  BarChart3
} from 'lucide-react';
import { AnimatedNumber } from '../components/AnimatedNumber';
import { WaitlistTopNav } from '../components/WaitlistTopNav';
import { Footer } from '../components/Footer';

export const AboutPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <WaitlistTopNav />
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-8">
            <Check className="w-4 h-4 mr-2" />
            Built with love in Canada
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            We're making financial tracking
            <span className="text-brand-500 block">simple for everyone</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            SimpleTracker was born from a simple frustration: why is it so hard to see your true net worth and understand if your investments are actually working?
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-6 text-gray-600 leading-relaxed">
                <p>
                  In 2023, our founder was spending every Sunday updating spreadsheets to track her net worth. 
                  Despite having multiple properties and investment accounts, she couldn't get a clear picture of her financial health.
                </p>
                <p>
                  The existing tools were either too complex, too expensive, or didn't work well with Canadian banks. 
                  So we built SimpleTracker - a tool that actually makes sense for Canadian investors.
                </p>
                <p>
                  Today, we help <AnimatedNumber value={51862} duration={1200} /> Canadians track their net worth, optimize their portfolios, and make smarter financial decisions.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-brand-50 rounded-2xl p-8 border border-green-100">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-700 font-medium">Founded in 2023</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-brand-500 rounded-full"></div>
                  <span className="text-gray-700 font-medium">{' '}<AnimatedNumber value={51862} duration={1200} />{' '}active users</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-700 font-medium">{' '}<AnimatedNumber value={48750280000} duration={1800} prefix="$" suffix="+" decimals={1} formatLargeNumbers={true} />{' '}assets tracked</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-brand-500 rounded-full"></div>
                  <span className="text-gray-700 font-medium">Built for Canadians</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-brand-100 text-brand-700 rounded-full text-sm font-medium mb-8">
            <Building2 className="w-4 h-4 mr-2" />
            Our Mission
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            To democratize financial intelligence
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-12">
            We believe everyone deserves to understand their financial health and make informed decisions about their money. 
            No more guessing, no more spreadsheets, no more financial stress.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Transparency</h3>
              <p className="text-gray-600">
                Clear, honest insights about your financial health without hidden fees or complicated jargon.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <User className="w-6 h-6 text-brand-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Security</h3>
              <p className="text-gray-600">
                Bank-level encryption and security standards to keep your financial data safe and private.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <BarChart3 className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Canadian-First</h3>
              <p className="text-gray-600">
                Built specifically for Canadian investors, with local bank integration and tax optimization.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">User-First Design</h3>
                <p className="text-gray-600">
                  Every feature is designed with real users in mind. We prioritize simplicity and clarity over complexity.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Data Privacy</h3>
                <p className="text-gray-600">
                  Your financial data belongs to you. We use bank-level encryption and never sell your information.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Continuous Improvement</h3>
                <p className="text-gray-600">
                  We're constantly learning from our users and improving SimpleTracker based on real feedback.
                </p>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Transparency</h3>
                <p className="text-gray-600">
                  Clear pricing, honest communication, and no hidden fees. What you see is what you get.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Canadian Focus</h3>
                <p className="text-gray-600">
                  Built specifically for Canadian investors, with local expertise and understanding of our unique financial landscape.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Community</h3>
                <p className="text-gray-600">
                  We're building more than a tool - we're building a community of financially savvy Canadians.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Get in Touch
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-12">
            Have questions? Want to share feedback? We'd love to hear from you.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Link to="/waitlist" className="w-6 h-6 text-green-600">
                  <LucideLink className="w-6 h-6 text-green-600" />
                </Link>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
              <p className="text-gray-600">hello@simpletracker.ca</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-brand-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Location</h3>
              <p className="text-gray-600">Toronto, Canada</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Response Time</h3>
              <p className="text-gray-600">Within 24 hours</p>
            </div>
          </div>
          
          <a
            href="/"
            className="inline-flex items-center px-8 py-4 bg-brand-500 text-white rounded-lg font-semibold hover:bg-brand-600 transition-colors"
          >
            Start Your Free Trial
          </a>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}; 